import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Box,
    Paper,
    Stack,
    Typography,
    Chip,
    IconButton,
    useTheme,
    Checkbox,
    Tooltip,
} from "@mui/material";
import { UserAvatarStack } from "../../../../components/ui/userChip";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    AccessTime as AccessTimeIcon,
    Event as EventIcon,
    Task as TaskIcon,
    Schedule as ScheduleIcon,
    PlayArrow as PlayIcon,
    PriorityHigh as PriorityIcon,
    Pause as PauseIcon,
    CheckCircle as CompleteIcon,
    Block as BlockIcon,
    AccountTree as GroupIcon,
    Lock as LockIcon,
} from "@mui/icons-material";

const TaskCalendar = ({
    tasks,
    projects,
    employees,
    taskScope,
    currentUserId,
    onEditTask,
    onDeleteTask,
    onStatusChange,
    onTaskClick,
    getProjectName,
    getEmployeeName,
    getTaskDescription,
    getStatusLabel,
    getStatusColor,
    getPriorityColor,
    getPriorityIcon,
    hasPermission,
    selectedTaskIds = [],
    onToggleSelectTask,
}) => {
    const theme = useTheme();
    const calendarRef = useRef(null);
    const hasAlignedCalendarRef = useRef(false);

    const formatDateShort = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        const day = date.getUTCDate();
        return `${day}/${month}/${String(year).slice(-2)}`;
    };

    const parseTaskDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return null;
        return new Date(
            Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
        );
    };

    const addDays = (date, days) => {
        const nextDate = new Date(date);
        nextDate.setUTCDate(nextDate.getUTCDate() + days);
        return nextDate;
    };

    const differenceInDays = (startDate, endDate) =>
        Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

    const formatDayName = (date) =>
        date.toLocaleDateString("en-US", {
            weekday: "short",
            timeZone: "UTC",
        });

    const formatDayNumber = (date) =>
        date.toLocaleDateString("en-US", {
            day: "2-digit",
            timeZone: "UTC",
        });

    const formatMonthName = (date) =>
        date.toLocaleDateString("en-US", {
            month: "short",
            timeZone: "UTC",
        });

    const getTaskStartDate = (task) =>
        parseTaskDate(task.start_date || task.due_date || task.created_at);

    const getTaskEndDate = (task) =>
        parseTaskDate(task.due_date || task.start_date || task.created_at);

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case "todo":
                return <ScheduleIcon sx={{ fontSize: 14 }} />;
            case "in_progress":
                return <PlayIcon sx={{ fontSize: 14 }} />;
            case "review":
                return <PriorityIcon sx={{ fontSize: 14 }} />;
            case "done":
                return <CompleteIcon sx={{ fontSize: 14 }} />;
            case "paused":
                return <PauseIcon sx={{ fontSize: 14 }} />;
            case "blocked":
                return <BlockIcon sx={{ fontSize: 14 }} />;
            default:
                return <TaskIcon sx={{ fontSize: 14 }} />;
        }
    };

    const today = parseTaskDate(new Date().toISOString());
    const taskDates = tasks
        .flatMap((task) => [getTaskStartDate(task), getTaskEndDate(task)])
        .filter(Boolean);
    const earliestTaskDate =
        taskDates.length > 0
            ? new Date(Math.min(...taskDates.map((date) => date.getTime())))
            : today;
    const latestTaskDate =
        taskDates.length > 0
            ? new Date(Math.max(...taskDates.map((date) => date.getTime())))
            : addDays(earliestTaskDate, 6);
    const calendarStart = new Date(
        Math.min(
            addDays(today, -30).getTime(),
            addDays(earliestTaskDate, -2).getTime(),
        ),
    );
    const calendarEnd = new Date(
        Math.max(
            addDays(today, 45).getTime(),
            addDays(latestTaskDate, 2).getTime(),
        ),
    );
    const visibleDayCount = differenceInDays(calendarStart, calendarEnd) + 1;
    const calendarDays = Array.from({ length: visibleDayCount }, (_, index) =>
        addDays(calendarStart, index),
    );
    const todayIndex = Math.max(0, differenceInDays(calendarStart, today));
    const columnWidth = 164;
    const laneHeight = 150;

    const packedTaskRows = tasks
        .map((task) => {
            const startDate = getTaskStartDate(task) || calendarStart;
            const endDate = getTaskEndDate(task) || startDate;
            const normalizedEndDate = endDate < startDate ? startDate : endDate;
            const startOffset = Math.max(
                0,
                differenceInDays(calendarStart, startDate),
            );
            const endOffset = Math.min(
                visibleDayCount - 1,
                differenceInDays(calendarStart, normalizedEndDate),
            );

            return {
                task,
                startOffset,
                endOffset,
                span: Math.max(1, endOffset - startOffset + 1),
            };
        })
        .sort((a, b) => {
            if (a.startOffset !== b.startOffset) return a.startOffset - b.startOffset;
            return b.span - a.span;
        });

    const laneEnds = [];
    const scheduledTasks = packedTaskRows.map((taskRow) => {
        let lane = laneEnds.findIndex((laneEnd) => laneEnd < taskRow.startOffset);
        if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(taskRow.endOffset);
        } else {
            laneEnds[lane] = taskRow.endOffset;
        }

        return {
            ...taskRow,
            lane,
        };
    });

    const calendarLaneCount = Math.max(1, laneEnds.length);

    useEffect(() => {
        if (tasks.length === 0) {
            hasAlignedCalendarRef.current = false;
            return;
        }

        if (!calendarRef.current || hasAlignedCalendarRef.current) return;

        hasAlignedCalendarRef.current = true;
        requestAnimationFrame(() => {
            if (!calendarRef.current) return;
            calendarRef.current.scrollLeft = todayIndex * columnWidth;
        });
    }, [columnWidth, tasks.length, todayIndex]);

    useEffect(() => {
        const el = calendarRef.current;

        if (!el || tasks.length === 0) return;

        const wheelHandler = (e) => {
            const scrollDelta = Math.abs(e.deltaX) > Math.abs(e.deltaY)
                ? e.deltaX
                : e.deltaY;
            const nextLeft = el.scrollLeft + scrollDelta;
            const canScrollLeft = scrollDelta < 0 && el.scrollLeft > 0;
            const canScrollRight =
                scrollDelta > 0 && el.scrollLeft < el.scrollWidth - el.clientWidth;

            if (!canScrollLeft && !canScrollRight) return;

            e.preventDefault();
            el.scrollLeft = nextLeft;
        };

        el.addEventListener("wheel", wheelHandler, {
            passive: false,
        });

        return () => {
            el.removeEventListener("wheel", wheelHandler);
        };
    }, [tasks.length, visibleDayCount]);

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "18px",
                overflow: "hidden",
                border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"}`,
                backgroundColor:
                    theme.palette.mode === "dark" ? "#111827" : "#ffffff",
                boxShadow:
                    theme.palette.mode === "dark"
                        ? "0 20px 60px rgba(0,0,0,0.35)"
                        : "0 20px 60px rgba(15,23,42,0.08)",
            }}
        >
            {/* <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={2}
                sx={{
                    p: 2.5,
                    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"}`,
                }}
            >
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            color:
                                theme.palette.mode === "dark" ? "#f8fafc" : "#111827",
                        }}
                    >
                        Task Calendar
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color:
                                theme.palette.mode === "dark" ? "#94a3b8" : "#6b7280",
                        }}
                    >
                        {formatMonthName(calendarDays[0])}{" "}
                        {calendarDays[0].getUTCFullYear()} timeline
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                        size="small"
                        icon={<EventIcon />}
                        label={`${calendarDays.length} days`}
                        sx={{
                            backgroundColor:
                                theme.palette.mode === "dark" ? "#1e293b" : "#fffbeb",
                            color: "#d97706",
                            fontWeight: 700,
                        }}
                    />
                    <Chip
                        size="small"
                        icon={<TaskIcon />}
                        label={`${tasks.length} scheduled`}
                        sx={{
                            backgroundColor:
                                theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
                            color:
                                theme.palette.mode === "dark" ? "#e5e7eb" : "#374151",
                            fontWeight: 700,
                        }}
                    />
                </Stack>
            </Stack> */}

            <Box
                id="task-board-container"
                ref={calendarRef}
                sx={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    width: "100%",
                    overscrollBehaviorX: "contain",
                    scrollbarGutter: "stable",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                        height: 10,
                    },
                    "&::-webkit-scrollbar-track": {
                        background:
                            theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background:
                            theme.palette.mode === "dark" ? "#475569" : "#cbd5e1",
                        borderRadius: 999,
                    },
                }}
            >
                <Box
                    sx={{
                        minWidth: calendarDays.length * columnWidth,
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${calendarDays.length}, ${columnWidth}px)`,
                        }}
                    >
                        {calendarDays.map((day) => {
                            const isToday = day.getTime() === today.getTime();
                            return (
                                <Box
                                    key={day.toISOString()}
                                    sx={{
                                        height: 88,
                                        p: 1.5,
                                        borderBottom: `1px solid ${theme.palette.mode === "dark"
                                            ? "#334155"
                                            : "#e5e7eb"
                                            }`,
                                        borderRight: `1px solid ${theme.palette.mode === "dark"
                                            ? "#1f2937"
                                            : "#f3f4f6"
                                            }`,
                                        backgroundColor: isToday
                                            ? theme.palette.mode === "dark"
                                                ? "rgba(245,158,11,0.16)"
                                                : "#fffbeb"
                                            : theme.palette.mode === "dark"
                                                ? "#111827"
                                                : "#ffffff",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                            color: isToday
                                                ? "#d97706"
                                                : theme.palette.mode === "dark"
                                                    ? "#94a3b8"
                                                    : "#6b7280",
                                        }}
                                    >
                                        {formatDayName(day)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.5,
                                            width: 34,
                                            height: 34,
                                            borderRadius: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "1rem",
                                            fontWeight: 900,
                                            backgroundColor: isToday ? "#f59e0b" : "transparent",
                                            color: isToday
                                                ? "#ffffff"
                                                : theme.palette.mode === "dark"
                                                    ? "#f8fafc"
                                                    : "#111827",
                                        }}
                                    >
                                        {formatDayNumber(day)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.5,
                                            fontSize: "0.68rem",
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? "#64748b"
                                                    : "#9ca3af",
                                        }}
                                    >
                                        {formatMonthName(day)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            minHeight: calendarLaneCount * laneHeight + 24,
                            position: "relative",
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#1f2937" : "#f3f4f6"
                                }`,
                            backgroundImage: `
                linear-gradient(to right, ${theme.palette.mode === "dark"
                                    ? "rgba(148,163,184,0.13)"
                                    : "rgba(148,163,184,0.24)"
                                } 1px, transparent 1px),
                linear-gradient(to bottom, ${theme.palette.mode === "dark"
                                    ? "rgba(148,163,184,0.08)"
                                    : "rgba(148,163,184,0.14)"
                                } 1px, transparent 1px)
              `,
                            backgroundSize: `${columnWidth}px 100%, 100% ${laneHeight}px`,
                        }}
                    >
                        {scheduledTasks.map(
                            ({ task, startOffset, span, lane }, index) => (
                                <motion.div
                                    key={task.id}
                                    initial={{ opacity: 0, scaleX: 0.94 }}
                                    animate={{ opacity: 1, scaleX: 1 }}
                                    transition={{ delay: index * 0.04 }}
                                    style={{
                                        position: "absolute",
                                        top: lane * laneHeight + 14,
                                        left: startOffset * columnWidth + 10,
                                        width: span * columnWidth - 20,
                                    }}
                                >
                                    <Box
                                        onClick={() => onTaskClick(task)}
                                        sx={{
                                            width: '100%',
                                            borderRadius: "12px",
                                            border: selectedTaskIds.includes(task.id)
                                                ? "2px solid #f59e0b"
                                                : `1px solid ${getStatusColor(task.status).color}`,
                                            borderLeft: `5px solid ${getPriorityColor(task.priority)}`,
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? `linear-gradient(135deg, ${getStatusColor(task.status).color}26 0%, rgba(17,24,39,0.94) 100%)`
                                                    : `linear-gradient(135deg, ${getStatusColor(task.status).bg} 0%, #ffffff 100%)`,
                                            boxShadow:
                                                theme.palette.mode === "dark"
                                                    ? "0 12px 30px rgba(0,0,0,0.3)"
                                                    : "0 12px 28px rgba(15,23,42,0.08)",
                                            cursor: "pointer",
                                            p: 1.25,
                                            minHeight: 95,
                                            overflow: "hidden",
                                            transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                            "&:hover": {
                                                // transform: "translateX(1px) scale(1.02)",
                                                transform: "translateX(-5px)",
                                                width: "calc(100% + 10px)",
                                                boxShadow:
                                                    `0 24px 48px ${getPriorityColor(task.priority)}40`,
                                                borderColor: getPriorityColor(task.priority),
                                            },
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            spacing={1}
                                            alignItems="flex-start"
                                        >
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.82rem",
                                                        fontWeight: 900,
                                                        color:
                                                            theme.palette.mode === "dark"
                                                                ? "#f8fafc"
                                                                : "#111827",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {task.title}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        color:
                                                            theme.palette.mode === "dark"
                                                                ? "#cbd5e1"
                                                                : "#6b7280",
                                                        mt: 0.5,
                                                        maxWidth: "500px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        whiteSpace: "normal",
                                                    }}
                                                >
                                                    {getTaskDescription(task) || "No description"}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.3} alignItems="center">
                                                {onToggleSelectTask && (
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedTaskIds.includes(task.id)}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onToggleSelectTask(task.id);
                                                        }}
                                                        sx={{
                                                            p: 0.25,
                                                            mr: 0.25,
                                                            color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
                                                            "&.Mui-checked": {
                                                                color: "#f59e0b",
                                                            },
                                                        }}
                                                    />
                                                )}
                                                {hasPermission("tasks.update") && (
                                                    <IconButton
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onEditTask(task);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            color: "primary.main",
                                                            backgroundColor:
                                                                theme.palette.mode === "dark"
                                                                    ? "rgba(245,158,11,0.12)"
                                                                    : "rgba(245,158,11,0.1)",
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.mode === "dark"
                                                                    ? "rgba(245,158,11,0.2)"
                                                                    : "rgba(245,158,11,0.15)",
                                                            }
                                                        }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                )}
                                                {hasPermission("tasks.delete") && (
                                                    <IconButton
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onDeleteTask(task);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            color: "#ef4444",
                                                            backgroundColor:
                                                                theme.palette.mode === "dark"
                                                                    ? "rgba(239,68,68,0.12)"
                                                                    : "rgba(239,68,68,0.08)",
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.mode === "dark"
                                                                    ? "rgba(239,68,68,0.2)"
                                                                    : "rgba(239,68,68,0.15)",
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </Stack>

                                        {/* Mother Task / Group Badge or Blocked Badge */}
                                        {(task.task_group_name || task.is_blocked) && (
                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                                                {task.task_group_name && (
                                                    <Chip
                                                        icon={<GroupIcon sx={{ fontSize: "12px !important" }} />}
                                                        label={`Group: ${task.task_group_name}`}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            fontSize: "0.65rem",
                                                            fontWeight: 600,
                                                            bgcolor: theme.palette.mode === "dark" ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.12)",
                                                            color: "#f59e0b",
                                                        }}
                                                    />
                                                )}
                                                {task.is_blocked && (
                                                    <Tooltip
                                                        title={
                                                            task.blocked_by && task.blocked_by.length > 0
                                                                ? `Waiting on: ${task.blocked_by.map(b => b.title).join(", ")}`
                                                                : "Prerequisite tasks must finish first"
                                                        }
                                                    >
                                                        <Chip
                                                            icon={<LockIcon sx={{ fontSize: "12px !important", color: "#ef4444 !important" }} />}
                                                            label={
                                                                task.blocked_by && task.blocked_by.length > 0
                                                                    ? `Locked: ${task.blocked_by[0].title}`
                                                                    : "Locked"
                                                            }
                                                            size="small"
                                                            sx={{
                                                                height: 18,
                                                                fontSize: "0.65rem",
                                                                fontWeight: 700,
                                                                bgcolor: theme.palette.mode === "dark" ? "rgba(239, 68, 68, 0.2)" : "rgba(254, 226, 226, 0.9)",
                                                                color: "#ef4444",
                                                                border: "1px solid rgba(239, 68, 68, 0.4)",
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        )}

                                        <Stack
                                            direction="row"
                                            spacing={0.75}
                                            alignItems="center"
                                            sx={{ mt: 1, flexWrap: "wrap" }}
                                            useFlexGap
                                        >
                                            <Chip
                                                icon={getStatusIcon(task.status)}
                                                label={getStatusLabel(task.status)}
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onStatusChange(event, task);
                                                }}
                                                sx={{
                                                    height: 20,
                                                    cursor: "pointer",
                                                    backgroundColor: getStatusColor(task.status).bg,
                                                    color: getStatusColor(task.status).color,
                                                    fontWeight: 800,
                                                    "& .MuiChip-icon": {
                                                        color: getStatusColor(task.status).color,
                                                        fontSize: 14,
                                                    },
                                                }}
                                            />
                                            <Chip
                                                icon={getPriorityIcon(task.priority)}
                                                label={task.priority || "Priority"}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    border: `1px solid ${getPriorityColor(
                                                        task.priority,
                                                    )}`,
                                                    backgroundColor: "transparent",
                                                    color: getPriorityColor(task.priority),
                                                    fontWeight: 800,
                                                }}
                                            />
                                        </Stack>
                                        {taskScope !== "assigned_to_me" && (
                                            <Stack
                                                direction="row"
                                                spacing={0.75}
                                                alignItems="center"
                                                sx={{ mt: 0.75 }}
                                                flexWrap="wrap"
                                            >
                                                {(() => {
                                                    let users = [];
                                                    if (task.assignees?.length > 0) {
                                                        users = task.assignees.map(a => ({
                                                            id: a.employee_id,
                                                            name: getEmployeeName(a.employee_id),
                                                            status: a.status
                                                        }));
                                                    } else if (task.assigned_to) {
                                                        users = [{
                                                            id: task.assigned_to,
                                                            name: getEmployeeName(task.assigned_to),
                                                            status: task.status
                                                        }];
                                                    }
                                                    return <UserAvatarStack users={users} />;
                                                })()}
                                            </Stack>
                                        )}
                                    </Box>
                                </motion.div>
                            ),
                        )}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export default TaskCalendar;